import { clsx } from "clsx";
import { splitStreetUnit, joinStreetUnit, addressUnitConflict } from "./address-units";
import { twMerge } from "tailwind-merge"
export { addressUnitConflict } from "./address-units";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatPhone(raw) {
  if (!raw) return "";
  let digits = String(raw).replace(/\D/g, "");
  if (digits.length === 11 && digits[0] === "1") {
    digits = digits.slice(1);
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return String(raw);
}

export function normalizePhoneForSave(raw) {
  let digits = String(raw || "").replace(/\D/g, "");
  if (digits.length === 11 && digits[0] === "1") {
    digits = digits.slice(1);
  }
  return digits.length === 10 ? digits : String(raw || "");
}

const DATE_ONLY_VISIT_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.0+)?(?:Z|\+00:00)?)?$/;

export function visitDateValue(dateStr) {
  if (!dateStr) return null;
  const match = String(dateStr).match(DATE_ONLY_VISIT_RE);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(dateStr);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function isDateOnlyVisit(dateStr) {
  return Boolean(dateStr && String(dateStr).match(DATE_ONLY_VISIT_RE));
}

export function formatVisitDate(dateStr, options) {
  const value = visitDateValue(dateStr);
  return value ? value.toLocaleDateString(undefined, options) : "—";
}

export function formatVisitDateTime(dateStr) {
  const value = visitDateValue(dateStr);
  if (!value) return "—";
  if (isDateOnlyVisit(dateStr)) return value.toLocaleDateString();
  return value.toLocaleString();
}

export function formatVisitTime(dateStr) {
  const value = visitDateValue(dateStr);
  if (!value || isDateOnlyVisit(dateStr)) return "";
  return value.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function isThisWeek(dateStr, now = new Date()) {
  if (!dateStr) return false;
  const d = visitDateValue(dateStr);
  if (!d) return false;
  const dow = now.getDay();
  const startOfWeek = new Date(now);
  startOfWeek.setHours(0, 0, 0, 0);
  startOfWeek.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1));
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);
  return d >= startOfWeek && d <= endOfWeek;
}

export function parseAddress(str) {
  if (!str || !str.trim()) {
    return { street: "", apt: "", city: "Pullman", state: "WA", zip: "" };
  }

  const parts = str.split(/,\s*/);
  let street = "", apt = "", city = "Pullman", state = "WA", zip = "";

  if (parts.length >= 3) {
    const stateZipMatch = parts[parts.length - 1].trim().match(/^([A-Za-z]{2})\s+(\d{5})$/);
    if (stateZipMatch) {
      state = stateZipMatch[1].toUpperCase();
      zip = stateZipMatch[2];
    }
    city = parts[parts.length - 2].trim();
    street = parts.slice(0, parts.length - 2).join(", ").trim();
  } else if (parts.length === 2) {
    const stateZipMatch = parts[1].trim().match(/([A-Za-z]{2})\s+(\d{5})$/);
    if (stateZipMatch) {
      state = stateZipMatch[1].toUpperCase();
      zip = stateZipMatch[2];
      city = parts[1].trim().slice(0, parts[1].trim().indexOf(stateZipMatch[0])).trim();
    }
    street = parts[0].trim();
  } else {
    street = str.trim();
  }

  ({ street, apt } = splitStreetUnit(street));

  return { street, apt, city, state, zip };
}

export function assembleAddress({ street, apt, city, state, zip }) {
  const trimStreet = (street || "").trim();
  const trimApt = (apt || "").trim();
  const trimCity = (city || "").trim();
  const trimState = (state || "").trim();
  const trimZip = (zip || "").trim();
  if (!trimStreet && !trimCity && !trimZip) return "";
  const streetPart = joinStreetUnit(trimStreet, trimApt);
  const stateZip = [trimState, trimZip].filter(Boolean).join(" ");
  return [streetPart, trimCity, stateZip].filter(Boolean).join(", ");
}

export function isAddressValid(str) {
  const fields = parseAddress(str || "");
  const { street, city, state, zip } = fields;
  return !!(street && city && state && /^\d{5}$/.test(zip) && !addressUnitConflict(fields));
}
